import React, { useState } from 'react';
import { Network, Sparkles, Building2, Globe2, Landmark, ArrowUpRight, ArrowRight } from 'lucide-react';

export const NETWORK_ROWS = [
  {
    category: 'Business & Strategic Partners',
    tag: 'Specialist Advisory · Market & Capital Services',
    icon: Building2,
    partners: [
      {
        name: 'DLS',
        fullName: 'DLS Advisory & Legal',
        role: 'Corporate Structuring & Legal Advisory',
        logo: null,
        desc: 'Specialist venture law, compliance, and institutional governance support.',
      },
      {
        name: 'ASM',
        fullName: 'ASM Partners',
        role: 'Financial Strategy & Tax Advisory',
        logo: null,
        desc: 'Venture audit, financial modeling, and fiscal management for scale.',
      },
      {
        name: 'Valserve',
        fullName: 'Valserve Consultants',
        role: 'Valuation & Transaction Advisory',
        logo: null,
        desc: 'Independent business valuations, deal structuring, and diligence readiness.',
      },
      {
        name: 'Gexpress',
        fullName: 'Gexpress Logistics & Supply',
        role: 'Supply Chain & Enterprise Distribution',
        logo: null,
        desc: 'E-commerce logistics, fulfillment networks, and multi-city distribution pathways.',
      },
    ],
  },
  {
    category: 'Chambers & Industry Networks',
    tag: 'Industry Bodies · Trade Influence · Ecosystem Voices',
    icon: Globe2,
    partners: [
      {
        name: 'TiE',
        fullName: 'The Indus Entrepreneurs (TiE)',
        role: 'Global Mentorship & Angel Network',
        logo: null,
        desc: 'Connecting founders to global charter members, mentors, and investor forums.',
      },
      {
        name: 'FICCI FLO',
        fullName: 'FICCI Ladies Organisation',
        role: 'Women Entrepreneurship & Policy Forum',
        logo: null,
        desc: 'India’s apex business chamber empowering women founders and enterprise leadership.',
      },
      {
        name: 'ASSOCHAM',
        fullName: 'Associated Chambers of Commerce of India',
        role: 'National Industry Chamber & Policy Access',
        logo: null,
        desc: 'National policy dialogues, corporate partnerships, and pan-India commercial scale.',
      },
      {
        name: 'ISBA',
        fullName: 'Indian Science & Technology Incubators Association',
        role: 'Incubator & Innovation Network',
        logo: null,
        desc: 'National association for technology business incubators and institutional parks.',
      },
    ],
  },
  {
    category: 'Incubators, Institutions & Capital',
    tag: 'State Programs · Academic Centres · Angel Syndicates',
    icon: Landmark,
    partners: [
      {
        name: 'iStart',
        fullName: 'iStart Rajasthan',
        role: 'Flagship State Startup Ecosystem',
        logo: '/partners/istart.jpg',
        desc: 'Government of Rajasthan startup initiative enabling funding, incubation and market access.',
      },
      {
        name: 'ACIC-VGU',
        fullName: 'Atal Community Innovation Centre - VGU',
        role: 'AIM NITI Aayog Innovation Centre',
        logo: '/partners/aic.webp',
        desc: 'Fostering grassroots innovation, maker spaces, and student-led startup incubation.',
      },
      {
        name: 'PIEDS',
        fullName: 'PIEDS BITS Pilani',
        role: 'Technology Business Incubator',
        logo: '/partners/pieds.webp',
        desc: 'BITS Pilani innovation and entrepreneurship centre driving deep-tech ventures.',
      },
      {
        name: 'i-Hub',
        fullName: 'i-Hub Innovation Hub',
        role: 'Institutional Incubation Ecosystem',
        logo: '/partners/i-hub.webp',
        desc: 'Student and researcher entrepreneurship hub bridging universities with industry.',
      },
      {
        name: 'FAAD',
        fullName: 'FAAD Network',
        role: 'Early-Stage Angel Syndicate',
        logo: null,
        desc: 'Leading angel investor network backing pre-seed and seed stage tech innovations.',
      },
      {
        name: 'Easy Knowledge Club',
        fullName: 'Easy Knowledge Club',
        role: 'Capability & Knowledge Platform',
        logo: null,
        desc: 'Curated business frameworks, founder learning circles, and domain masterclasses.',
      },
      {
        name: 'NEN Wadhwani Foundation',
        fullName: 'NEN Wadhwani Foundation',
        role: 'Global Entrepreneurship Initiative',
        logo: null,
        desc: 'National Entrepreneurship Network driving structured venture curricula and scale.',
      },
    ],
  },
];

function PartnerCard({ partner, categoryName }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-lg hover:shadow-[#d42b92]/10">
      {/* Subtle hover pink aura */}
      <div className="absolute -top-12 -right-12 w-28 h-28 rounded-full bg-[#d42b92]/0 group-hover:bg-[#d42b92]/15 blur-2xl transition-all duration-500 pointer-events-none" />

      <div>
        {/* Header: Logo / Monogram Badge + Arrow */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="h-12 px-3 rounded-xl border border-white/10 bg-white/[0.04] flex items-center justify-center overflow-hidden">
            {!imgError && partner.logo ? (
              <img
                src={partner.logo}
                alt={partner.name}
                onError={() => setImgError(true)}
                className="max-h-8 max-w-[120px] object-contain filter grayscale brightness-125 group-hover:grayscale-0 transition-all duration-300"
                loading="lazy"
              />
            ) : (
              <span className="font-mono font-bold text-sm sm:text-base text-[#ff8ec5] tracking-wider">
                {partner.name}
              </span>
            )}
          </div>

          <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/10 text-white/50 group-hover:border-[#d42b92]/40 group-hover:text-[#ff8ec5] transition-colors">
            {categoryName}
          </span>
        </div>

        {/* Full Name & Role */}
        <h4 className="text-base sm:text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-snug">
          {partner.fullName}
        </h4>
        <p className="mt-1 text-xs font-medium text-white/60">
          {partner.role}
        </p>

        {/* Narrative Description */}
        <p className="mt-3 text-xs sm:text-[13px] text-white/80 leading-relaxed font-normal">
          {partner.desc}
        </p>
      </div>

      {/* Card Footer */}
      <div className="mt-5 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-white/40">
        <span className="text-[11px] font-mono tracking-widest uppercase text-white/50">
          {partner.name}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-[#ff8ec5] opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
          <span>Active Partner</span>
          <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
}

export default function TheNetworkSection() {
  const [activeTab, setActiveTab] = useState('All');

  const categories = [
    'All',
    'Business & Strategic Partners',
    'Chambers & Industry Networks',
    'Incubators, Institutions & Capital',
  ];

  const filteredGroups =
    activeTab === 'All'
      ? NETWORK_ROWS
      : NETWORK_ROWS.filter((g) => g.category === activeTab);

  return (
    <section id="the-network-section" className="relative bg-[#08080a] text-white py-20 lg:py-28 border-t border-white/5 overflow-hidden">
      {/* Ambient pink backlights */}
      <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-[500px] h-[500px] rounded-full bg-[#d42b92]/8 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top Header & Narrative Area */}
        <div className="max-w-4xl reveal-up">
          {/* Eyebrow */}
          {/* <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" />
            <span>THE NETWORK</span>
          </div> */}

          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
            Connected to move <span className="text-[#d42b92]">ventures further.</span>
          </h2>

          {/* Subtitle statement */}
          <p className="mt-6 text-xl sm:text-2xl font-medium text-white/90 leading-snug">
            Venture building becomes more powerful when the right ecosystems work together.
          </p>

          {/* Body Paragraphs */}
          <div className="mt-6 space-y-5 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
            <p>
              InnovHer collaborates with a curated business networks, incubators, investor communities, institutions and strategic partners that extend the opportunities available to its ventures. These relationships create pathways for venture discovery, specialist support, market access, co-investment, partnerships and growth.
            </p>
            <p className="text-white/90 font-medium">
              The emphasis is not on building the largest network, but on building one that participates.
            </p>
            <p>
              Selected partners bring distinct capabilities and access points into the InnovHer ecosystem, creating a distributed network through which ventures can find the right connection, at the right stage, for the right opportunity.
            </p>
          </div>
        </div>

        {/* Category Filters Bar */}
        {/* <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5 reveal-up">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-[#ff8ec5]" />
            <span className="text-sm font-semibold text-white tracking-wide">
              Ecosystem Access Points &amp; Organizations
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const active = activeTab === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveTab(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider transition-all ${active
                    ? 'bg-[#d42b92] text-white shadow-md shadow-[#d42b92]/20'
                    : 'border border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/20'
                    }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div> */}

        {/* Partner Groups & Cards */}
        {/* <div className="mt-8 space-y-12 reveal-up">
          {filteredGroups.map((group) => {
            const Icon = group.icon;
            return (
              <div key={group.category} className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#d42b92]/20 border border-[#d42b92]/40 flex items-center justify-center text-[#ff8ec5]">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">
                      {group.category}
                    </h3>
                    <p className="text-xs text-white/50">
                      {group.tag}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {group.partners.map((partner) => (
                    <PartnerCard
                      key={partner.name}
                      partner={partner}
                      categoryName={group.category.split(' ')[0]}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div> */}

        {/* Bottom Banner Note */}
        {/* <div className="mt-16 rounded-2xl border border-white/10 bg-gradient-to-r from-[#d42b92]/15 via-white/[0.02] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 reveal-up shadow-xl">
          <div>
            <h4 className="text-white font-bold text-base sm:text-lg">
              Explore Institutional, Academic &amp; Strategic Partnerships
            </h4>
            <p className="mt-1 text-xs sm:text-sm text-white/70 max-w-2xl">
              Connecting ventures to relevant expertise, perspectives and relationships at different stages of their journey.
            </p>
          </div>
          <a
            href="/contact?intent=partner"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#d42b92] hover:bg-[#e0339f] text-white text-xs sm:text-sm font-semibold tracking-wide transition-colors whitespace-nowrap shadow-lg shadow-[#d42b92]/20"
          >
            <span>Partner with InnovHer</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div> */}
      </div>
    </section>
  );
}
